import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Card,
  Accordion,
  AccordionSummary,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import Div from "@jumbo/shared/Div";
import JumboSearch from "@jumbo/components/JumboSearch";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import NoDataPlaceholder from "app/shared/NoDataPlaceholder";
import {
  getCalculationsModel,
  saveCalculationsModel,
} from "../../services/api/calculations";

// Import shared cache utility
import { calculationsCache } from "../../utils/cache";

// Import drag & drop libraries
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Check if this is a hard reload (not SPA navigation)
const isHardReload = () => {
  try {
    const navigation = performance.getEntriesByType("navigation")[0];
    return navigation?.type === "reload" || navigation?.type === "navigate";
  } catch (e) {
    // Fallback: if performance API is not available, assume reload
    return true;
  }
};

// Sortable Product Item Component
const SortableProductItem = ({
  product,
  index,
  t,
  ProductImage,
  getProductDisplayName,
  currencyCode,
  defaultLanguage,
  handleProductDetail,
  handleDeleteClick,
  handleDuplicateClick,
  deletingProduct,
  duplicatingProduct,
  onTogglePricelistVisibility,
  isSavingOrder,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.productKey });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : "auto",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card sx={{ mb: 1, cursor: isDragging ? "grabbing" : "grab" }}>
        <Accordion expanded={false} square sx={{ borderRadius: 2 }}>
          <AccordionSummary
            aria-controls="panel1bh-content"
            id="panel1bh-header"
            sx={{
              px: 3,
              flexDirection: "row-reverse",
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                justifyContent: "space-between",

                "&.Mui-expanded": {
                  margin: "12px 0",
                },
              },
              ".MuiAccordionSummary-expandIconWrapper": {
                color: "text.secondary",
                transform: "none",
                height: 28,
                width: 28,
                alignItems: "center",
                justifyContent: "center",
                mr: 1,

                "&.Mui-expanded": {
                  transform: "none",
                  color: "primary.main",
                  borderColor: "primary.main",
                },

                "& svg": {
                  fontSize: "1.25rem",
                },
              },
            }}
          >
            {/* Image Column */}
            <Div
              sx={{
                flex: { xs: "0 1 auto", md: 0.25 },
                flexShrink: 0,
                px: 1,
              }}
            >
              <Typography
                fontSize={"12px"}
                variant={"h6"}
                color={"text.secondary"}
                mb={0.5}
                sx={{
                  display: { xs: "none", md: "block" },
                }}
              >
                {t("pages.products.image")}
              </Typography>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  height: "75px",
                  alignItems: "center",
                }}
              >
                <ProductImage
                  src={product.imageUrl}
                  alt={product.name}
                  variant="list"
                />
              </div>
            </Div>

            {/* Name Column */}
            <Div sx={{ flex: { xs: "0 1 auto", md: 1 }, flexShrink: 0, px: 1 }}>
              <Typography
                fontSize={"12px"}
                variant={"h6"}
                color={"text.secondary"}
                mb={0.25}
                sx={{
                  display: { xs: "none", md: "block" },
                }}
              >
                {t("pages.presentation.label")}
              </Typography>
              <Typography variant={"body1"}>
                {getProductDisplayName(product, defaultLanguage)}
              </Typography>
              <Div sx={{ mt: 0.25, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {product.customProductId && (
                  <Typography
                    variant={"caption"}
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}
                  >
                    ID: {product.customProductId}
                  </Typography>
                )}
              </Div>
            </Div>

            {/* Status Column */}
            <Div
              sx={{ flex: { xs: "0 1 auto", md: 0.5 }, flexShrink: 0, px: 1 }}
            >
              <Typography
                fontSize={"12px"}
                variant={"h6"}
                color={"text.secondary"}
                mb={0.25}
                sx={{
                  display: { xs: "none", md: "block" },
                }}
              >
                {t("pages.calculations.showInPricelist", {
                  defaultValue: "Zobraziť v cenníku",
                })}
              </Typography>
              <Box
                sx={{ display: "inline-flex", alignItems: "center" }}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip
                  title={t("pages.calculations.showInPricelist", {
                    defaultValue: "Zobraziť v cenníku",
                  })}
                >
                  <Switch
                    size="small"
                    checked={Boolean(product.isVisibleInPricelist)}
                    disabled={Boolean(isSavingOrder)}
                    onChange={(e) =>
                      onTogglePricelistVisibility?.(
                        product.productKey,
                        e.target.checked,
                      )
                    }
                  />
                </Tooltip>
              </Box>
            </Div>

            {/* Price Column */}
            <Div
              sx={{ flex: { xs: "0 1 auto", md: 0.5 }, flexShrink: 0, px: 1 }}
            >
              <Typography
                fontSize={"12px"}
                variant={"h6"}
                color={"text.secondary"}
                mb={0.25}
                sx={{
                  display: { xs: "none", md: "block" },
                }}
              >
                {t("pages.products.price")}
              </Typography>
              <Typography variant={"body1"}>
                {Number(product.price).toFixed(2)} {currencyCode || ""}
              </Typography>
            </Div>

            {/* Actions Column */}
            <Div
              sx={{
                flex: { xs: "0 2 auto", md: 0 },
                px: 1,
              }}
            >
              <Typography
                fontSize={"12px"}
                variant={"h6"}
                color={"text.secondary"}
                mb={0.25}
                sx={{
                  display: { xs: "none", md: "block" },
                }}
              >
                {t("pages.presentation.actions")}
              </Typography>
              <Div
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 0.5,
                  minWidth: 70,
                }}
              >
                <Tooltip title="Upraviť">
                  <IconButton
                    size="small"
                    variant={"contained"}
                    onClick={() => handleProductDetail(product.productKey)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Duplikovať">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleDuplicateClick(product)}
                      disabled={duplicatingProduct === product.productKey}
                    >
                      {duplicatingProduct === product.productKey ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                {product?.pdfDocumentationUrl ? (
                  <Tooltip title="PDF dokumentácia">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(
                          product.pdfDocumentationUrl,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                    >
                      <PictureAsPdfIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteClick(product)}
                  disabled={deletingProduct === product.productKey}
                  sx={{
                    "&:hover": {
                      backgroundColor: "error.main",
                      color: "error.contrastText",
                    },
                  }}
                >
                  <Tooltip title="Zmazať">
                    <span>
                      {deletingProduct === product.productKey ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <DeleteForeverIcon />
                      )}
                    </span>
                  </Tooltip>
                </IconButton>
              </Div>
            </Div>
          </AccordionSummary>
        </Accordion>
      </Card>
    </div>
  );
};

// Helper to normalize isVisibleInPricelist boolean value
const normalizeIsVisibleInPricelist = (value) => {
  if (value === undefined || value === null) return true; // Default to visible
  if (typeof value === "string") {
    return value.toLowerCase() !== "false" && value !== "0";
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  return Boolean(value); // For actual booleans
};

const Calculations = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const currentYear = new Date().getFullYear();

  // Default values for currency and language
  const currencyCode = "€";
  const defaultLanguage = "sk";
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  // Keep the canonical full model from API/cache to avoid reconstructing products on save
  const [calculationsModelFull, setCalculationsModelFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [productToDuplicate, setProductToDuplicate] = useState(null);
  const [duplicatingProduct, setDuplicatingProduct] = useState(null);
  const [priceListModalOpen, setPriceListModalOpen] = useState(false);
  const [selectedPriceType, setSelectedPriceType] = useState(null); // 'moc', 'voc-l1', 'voc-l2', 'voc-l3'
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [pricelistVisibilityChanges, setPricelistVisibilityChanges] = useState(
    {},
  ); // { [productKey]: boolean }
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [createProductModalOpen, setCreateProductModalOpen] = useState(false);
  const [newProductData, setNewProductData] = useState({
    name: "",
    image: "",
    url: "",
    code: "",
    productType: "",
  });
  const [newProductErrors, setNewProductErrors] = useState({});
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const isMounted = useRef(true);

  // Handle drag end for product reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setProducts((items) => {
        const oldIndex = items.findIndex(
          (item) => item.productKey === active.id,
        );
        const newIndex = items.findIndex((item) => item.productKey === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Check if order actually changed
        const currentOrder = newItems.map((p) => p.productKey);
        const orderChanged = !originalOrder.every(
          (key, index) => key === currentOrder[index],
        );

        setHasOrderChanges(orderChanged);

        return newItems;
      });

      setFilteredProducts((items) => {
        const oldIndex = items.findIndex(
          (item) => item.productKey === active.id,
        );
        const newIndex = items.findIndex((item) => item.productKey === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const hasPricelistVisibilityChanges =
    Object.keys(pricelistVisibilityChanges).length > 0;

  const resolveBaseModel = () => {
    return calculationsModelFull || calculationsCache.data;
  };

  const resolveBasePricelistVisibility = (productKey) => {
    const base = resolveBaseModel();
    const raw = base?.products?.[productKey]?.isVisibleInPricelist;
    return normalizeIsVisibleInPricelist(raw);
  };

  const handleTogglePricelistVisibility = (productKey, checked) => {
    // Update local UI state immediately (staged, persisted only on save)
    setProducts((prev) =>
      prev.map((p) =>
        p.productKey === productKey
          ? { ...p, isVisibleInPricelist: checked }
          : p,
      ),
    );
    setFilteredProducts((prev) =>
      prev.map((p) =>
        p.productKey === productKey
          ? { ...p, isVisibleInPricelist: checked }
          : p,
      ),
    );

    // Track only diffs vs base model to avoid unintended mass changes
    const baseValue = resolveBasePricelistVisibility(productKey);
    setPricelistVisibilityChanges((prev) => {
      const next = { ...prev };
      if (checked === baseValue) {
        delete next[productKey];
      } else {
        next[productKey] = checked;
      }
      return next;
    });
  };

  // Handle saving product order
  const handleSaveOrder = async () => {
    if (!hasOrderChanges && !hasPricelistVisibilityChanges) return;

    setIsSavingOrder(true);
    try {
      const baseModel = calculationsModelFull || calculationsCache.data;
      const baseProducts = baseModel?.products;

      if (!baseModel || !baseProducts || typeof baseProducts !== "object") {
        enqueueSnackbar(
          "Nepodarilo sa uložiť poradie: chýbajú dáta modelu. Skúste obnoviť stránku.",
          { variant: "error" },
        );
        return;
      }

      // Build order map from current UI order (0-based) only when needed
      const orderMap = {};
      if (hasOrderChanges) {
        products.forEach((p, idx) => {
          if (p?.productKey) orderMap[p.productKey] = idx;
        });
      }

      // Deep clone full model and apply ONLY order updates.
      const updatedModel =
        typeof structuredClone === "function"
          ? structuredClone(baseModel)
          : JSON.parse(JSON.stringify(baseModel));

      const updatedProducts = updatedModel.products || {};

      // Apply only order changes, preserve all other fields untouched
      if (hasOrderChanges) {
        Object.keys(updatedProducts).forEach((productKey) => {
          if (Object.prototype.hasOwnProperty.call(orderMap, productKey)) {
            if (
              updatedProducts[productKey] &&
              typeof updatedProducts[productKey] === "object"
            ) {
              updatedProducts[productKey].order = orderMap[productKey];
            }
          }
        });
      }

      // Apply only "show in pricelist" diffs (do not touch products without explicit change)
      if (hasPricelistVisibilityChanges) {
        Object.keys(pricelistVisibilityChanges).forEach((productKey) => {
          if (
            updatedProducts[productKey] &&
            typeof updatedProducts[productKey] === "object"
          ) {
            updatedProducts[productKey].isVisibleInPricelist =
              pricelistVisibilityChanges[productKey];
          }
        });
      }

      // Safety check: ensure we didn't drop any top-level fields per product (esp. components/BOM)
      const requiredNestedKeys = ["components", "inputs", "bom"];
      const baseProductKeys = Object.keys(baseProducts);
      const updatedProductKeys = Object.keys(updatedProducts);

      if (baseProductKeys.length !== updatedProductKeys.length) {
        throw new Error("INVALID_PAYLOAD_PRODUCTS_KEYS");
      }

      for (const productKey of baseProductKeys) {
        const baseProduct = baseProducts[productKey];
        const nextProduct = updatedProducts[productKey];
        if (!nextProduct || typeof nextProduct !== "object") {
          throw new Error("INVALID_PAYLOAD_PRODUCT_MISSING");
        }

        // Ensure all top-level keys from base exist on payload product
        for (const k of Object.keys(baseProduct || {})) {
          if (!(k in nextProduct)) {
            throw new Error("INVALID_PAYLOAD_PRODUCT_DROPPED_FIELDS");
          }
        }

        // Ensure nested structures aren't dropped if they exist in base
        for (const nk of requiredNestedKeys) {
          if (
            baseProduct &&
            baseProduct[nk] !== undefined &&
            !(nk in nextProduct)
          ) {
            throw new Error("INVALID_PAYLOAD_PRODUCT_DROPPED_COMPONENTS");
          }
        }
      }

      const response = await saveCalculationsModel(updatedModel);

      if (response?.ok === true) {
        // Update cache with new data
        calculationsCache.set(updatedModel);
        setCalculationsModelFull(updatedModel);

        // Update local state
        setOriginalOrder(products.map((p) => p.productKey));
        setHasOrderChanges(false);
        setPricelistVisibilityChanges({});

        enqueueSnackbar("Zmeny boli úspešne uložené", {
          variant: "success",
        });
      } else {
        throw new Error("Save failed");
      }
    } catch (error) {
      console.error("Error saving product order:", error);
      enqueueSnackbar("Nepodarilo sa uložiť poradie produktov", {
        variant: "error",
      });
    } finally {
      setIsSavingOrder(false);
    }
  };

  // Validate URL format
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Validate create product form
  const validateNewProduct = (data, existingProducts) => {
    const errors = {};

    if (!data.name.trim()) {
      errors.name = "Názov produktu je povinný";
    }

    if (!data.image.trim()) {
      errors.image = "URL obrázka je povinný";
    } else if (!isValidUrl(data.image)) {
      errors.image = "Neplatný formát URL";
    }

    if (data.url.trim() && !isValidUrl(data.url)) {
      errors.url = "Neplatný formát URL";
    }

    if (!data.code.trim()) {
      errors.code = "Kód produktu je povinný";
    } else {
      // Check if code already exists
      const normalizedCode = data.code
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "_");
      const existingCodes = existingProducts.map((p) =>
        p.code?.toLowerCase().replace(/[^a-z0-9]/g, "_"),
      );
      if (existingCodes.includes(normalizedCode)) {
        errors.code = "Kód produktu už existuje";
      }
    }

    if (!data.productType) {
      errors.productType = "Typ produktu je povinný";
    }

    return errors;
  };

  // Handle create product
  const handleCreateProduct = async () => {
    const trimmedData = {
      name: newProductData.name.trim(),
      image: newProductData.image.trim(),
      url: newProductData.url.trim(),
      code: newProductData.code.trim(),
      productType: newProductData.productType,
    };

    const errors = validateNewProduct(trimmedData, products);
    setNewProductErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsCreatingProduct(true);

    try {
      // Generate unique key from code with collision handling
      let productKey = trimmedData.code
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "_");
      let counter = 1;
      const existingProducts = calculationsCache.data?.products || {};

      // Ensure unique product key
      while (existingProducts[productKey]) {
        productKey = `${trimmedData.code
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "_")}_${counter}`;
        counter++;
      }

      // Calculate order (find max order from existing products)
      const existingProductOrders = Object.values(existingProducts).map((p) =>
        typeof p.order === "number" ? p.order : 0,
      );
      const maxOrder =
        existingProductOrders.length > 0
          ? Math.max(...existingProductOrders)
          : 0;
      const newOrder = maxOrder + 1;

      // Create new product with default structure
      const newProduct = {
        _id: productKey,
        name: trimmedData.name,
        image: trimmedData.image,
        url: trimmedData.url || undefined,
        code: trimmedData.code,
        productType: trimmedData.productType,
        order: newOrder,
        description: "",
        components: [],
        pricingSettings: {
          targetMarginPercent: 35,
          wholesaleDiscounts: { l1: 10, l2: 15, l3: 20 },
          rounding: { enabled: true, method: "nearest", precision: 1 },
          validation: { preventBelowCogs: true },
          exchangeRates: { EUR_CZK: 25.0 },
        },
        parameters: {
          weightKg: 0,
          widthMm: 0,
          heightMm: 0,
          depthMm: 0,
        },
        isVisibleInPricelist: true,
        calculated: null, // Will be computed when editing
      };

      // Create updated model for API
      const updatedModel = {
        ...calculationsCache.data,
        products: {
          ...calculationsCache.data.products,
          [productKey]: newProduct,
        },
      };

      // Save to API using PUT
      const saveResponse = await saveCalculationsModel(updatedModel);

      if (saveResponse?.ok === true) {
        // Update cache with saved data
        calculationsCache.set(updatedModel);

        // Re-process data to get proper sorting
        processCalculationsData(updatedModel);

        // Update original order
        setOriginalOrder(
          Object.keys(updatedModel.products).sort((a, b) => {
            const orderA = updatedModel.products[a].order || 999999;
            const orderB = updatedModel.products[b].order || 999999;
            if (orderA !== orderB) return orderA - orderB;
            return (updatedModel.products[a].name || "").localeCompare(
              updatedModel.products[b].name || "",
              "sk",
            );
          }),
        );

        // Close modal and navigate
        setCreateProductModalOpen(false);
        setNewProductData({
          name: "",
          image: "",
          url: "",
          code: "",
          productType: "",
        });
        setNewProductErrors({});

        enqueueSnackbar("Produkt bol úspešne vytvorený", {
          variant: "success",
        });

        // Navigate to product detail
        navigate(`/calculations/${productKey}`);
      } else {
        throw new Error("API save failed");
      }
    } catch (error) {
      console.error("Error creating product:", error);
      if (error.message === "API save failed") {
        enqueueSnackbar("Nepodarilo sa uložiť produkt na server", {
          variant: "error",
        });
      } else {
        enqueueSnackbar("Nepodarilo sa vytvoriť produkt", {
          variant: "error",
        });
      }
    } finally {
      setIsCreatingProduct(false);
    }
  };

  const getProductDisplayName = (product, language = defaultLanguage) => {
    const productName = product?.productName;
    const fallbackName = product?.name;
    if (typeof productName === "string") {
      return productName;
    }
    if (
      productName &&
      typeof productName === "object" &&
      !Array.isArray(productName)
    ) {
      const langValue = productName?.[language];
      if (typeof langValue === "string") {
        return langValue;
      }
      const anyString = Object.values(productName).find(
        (v) => typeof v === "string" && v,
      );
      if (anyString) return anyString;
    }
    return typeof fallbackName === "string" ? fallbackName : "";
  };

  // Process calculations data and update state
  const processCalculationsData = (data) => {
    if (!data) return;
    // Preserve full model for safe order-only saves
    setCalculationsModelFull(data);

    // Extract products from the model
    const productsObject = data.products || {};
    const productsArray = Object.keys(productsObject)
      .map((productKey) => {
        const product = productsObject[productKey];
        const pdfDocumentationUrlRaw = product?.pdfDocumentationUrl;
        const pdfDocumentationUrl =
          typeof pdfDocumentationUrlRaw === "string" &&
          pdfDocumentationUrlRaw.trim()
            ? pdfDocumentationUrlRaw.trim()
            : undefined;
        return {
          productKey,
          name: product.name || "Neznámy produkt",
          price: product.calculated?.variantB?.retailEur ?? null,
          imageUrl:
            product.image ||
            product.meta?.imageUrl ||
            product.meta?.image ||
            "",
          calculated: product.calculated,
          description: product.description,
          pdfDocumentationUrl,
          isVisibleInPricelist: normalizeIsVisibleInPricelist(
            product.isVisibleInPricelist,
          ),
          order: typeof product.order === "number" ? product.order : 999999, // Default high order for missing order
        };
      })
      // Sort by order first, then by name for stable ordering (SK locale)
      .sort((a, b) => {
        const orderA = a.order;
        const orderB = b.order;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        // Fallback to name sorting for items with same order
        return a.name.localeCompare(b.name, "sk");
      });

    if (isMounted.current) {
      setProducts(productsArray);
      setFilteredProducts(productsArray);
      // Store original order for change detection
      setOriginalOrder(productsArray.map((p) => p.productKey));
      setHasOrderChanges(false);
      setPricelistVisibilityChanges({});
    }
  };

  // Drag & drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    isMounted.current = true;
    const fetchData = async (force = false) => {
      try {
        if (isMounted.current) setLoading(true);
        if (isMounted.current) setError(null);

        // Check cache first (unless force refresh)
        if (
          !force &&
          calculationsCache.isLoaded &&
          !calculationsCache.isExpired &&
          calculationsCache.data
        ) {
          console.log("Using cached calculations data");
          processCalculationsData(calculationsCache.data);
          if (isMounted.current) setLoading(false);
          return;
        }

        console.log("Fetching calculations data from API");
        const response = await getCalculationsModel();

        if (response.ok === true && response.data) {
          // Store in cache
          calculationsCache.set(response.data);

          // Process and display the data
          processCalculationsData(response.data);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        console.error("Error fetching calculations model:", err);
        if (isMounted.current) setError("Nepodarilo sa načítať produkty.");
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    // Decide whether to fetch from API or use cache
    const shouldFetchFromAPI =
      isHardReload() ||
      !calculationsCache.isLoaded ||
      calculationsCache.isExpired;

    if (shouldFetchFromAPI) {
      console.log("Hard reload or cache empty/expired - fetching from API");
      fetchData();
    } else {
      console.log("SPA navigation with valid cache - using cached data");
      processCalculationsData(calculationsCache.data);
    }

    // Expose refresh function globally for debugging/manual refresh
    window.refreshCalculationsCache = () => {
      console.log("Manually refreshing calculations cache");
      calculationsCache.clear();
      fetchData(true); // Force refresh
    };

    // Refresh data when window regains focus (user returns to tab) - but respect cache
    const handleFocus = () => {
      // Only fetch if cache is expired or empty
      if (calculationsCache.isExpired || !calculationsCache.isLoaded) {
        fetchData();
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      isMounted.current = false;
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  // Filter products based on search
  useEffect(() => {
    if (!searchName || searchName.trim() === "") {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter((product) => {
        const searchLower = searchName.toLowerCase();
        return (
          product.name.toLowerCase().includes(searchLower) ||
          product.productKey.toLowerCase().includes(searchLower)
        );
      });
      setFilteredProducts(filtered);
    }
  }, [searchName, products]);

  const handleProductDetail = (productKey) => {
    navigate(`/calculations/products/${productKey}`);
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDuplicateClick = (product) => {
    setProductToDuplicate(product);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateCancel = () => {
    setDuplicateDialogOpen(false);
    setProductToDuplicate(null);
  };

  const appendCopySuffixToName = (name) => {
    if (!name) return "Kópia";
    if (typeof name === "string") return `${name} (kópia)`;
    if (typeof name === "object") {
      const next = Array.isArray(name) ? [...name] : { ...name };
      Object.keys(next).forEach((k) => {
        if (typeof next[k] === "string") next[k] = `${next[k]} (kópia)`;
      });
      return next;
    }
    return String(name);
  };

  const normalizeCode = (code) =>
    String(code || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "_");

  const buildUniqueKey = (baseKey, existingProductsMap) => {
    const suffix = Math.random().toString(36).slice(2, 6);
    let key = `${baseKey}_copy_${suffix}`;
    let counter = 1;
    while (existingProductsMap[key]) {
      key = `${baseKey}_copy_${suffix}_${counter}`;
      counter += 1;
    }
    return key;
  };

  const buildUniqueCode = (baseCode, existingCodesLower) => {
    const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
    let candidate = `${baseCode}-COPY-${suffix}`;
    let counter = 1;
    while (existingCodesLower.has(String(candidate).toLowerCase())) {
      candidate = `${baseCode}-COPY-${suffix}-${counter}`;
      counter += 1;
    }
    return candidate;
  };

  const handleDuplicateConfirm = async () => {
    if (!productToDuplicate?.productKey) return;
    const originalKey = productToDuplicate.productKey;

    setDuplicatingProduct(originalKey);
    setDuplicateDialogOpen(false);

    try {
      // Fetch latest full model to guarantee deep copy includes components/BOM/etc.
      const response = await getCalculationsModel();
      if (!response?.ok || !response?.data) {
        throw new Error("FAILED_TO_FETCH_MODEL");
      }

      const currentModel = response.data;
      const currentProducts = currentModel.products || {};
      const originalProduct = currentProducts[originalKey];

      if (!originalProduct || typeof originalProduct !== "object") {
        throw new Error("ORIGINAL_PRODUCT_NOT_FOUND");
      }

      const originalJsonBefore = JSON.stringify(originalProduct);

      // Generate unique productKey/_id + code
      const existingCodesLower = new Set(
        Object.values(currentProducts)
          .map((p) => String(p?.code || "").toLowerCase())
          .filter(Boolean),
      );

      const baseKey = normalizeCode(
        originalProduct.code || originalKey || "product",
      );
      const newKey = buildUniqueKey(baseKey, currentProducts);
      const baseCodeRaw =
        String(originalProduct.code || baseKey).trim() || baseKey;
      const newCode = buildUniqueCode(baseCodeRaw, existingCodesLower);

      // Place duplicated product at the end
      const existingOrders = Object.values(currentProducts).map((p) =>
        typeof p?.order === "number" ? p.order : 0,
      );
      const maxOrder = existingOrders.length ? Math.max(...existingOrders) : 0;
      const newOrder = maxOrder + 1;

      // Deep copy the original product
      const clonedProduct =
        typeof structuredClone === "function"
          ? structuredClone(originalProduct)
          : JSON.parse(JSON.stringify(originalProduct));

      // Identity changes only
      clonedProduct._id = newKey;
      if (typeof clonedProduct.id !== "undefined") clonedProduct.id = newKey;
      clonedProduct.code = newCode;
      clonedProduct.name = appendCopySuffixToName(clonedProduct.name);
      clonedProduct.order = newOrder;

      // Deep clone full model and append the duplicated product
      const updatedModel =
        typeof structuredClone === "function"
          ? structuredClone(currentModel)
          : JSON.parse(JSON.stringify(currentModel));

      if (!updatedModel.products || typeof updatedModel.products !== "object") {
        updatedModel.products = {};
      }

      // Safety check: original must remain unchanged
      const originalJsonAfter = JSON.stringify(
        updatedModel.products[originalKey],
      );
      if (originalJsonBefore !== originalJsonAfter) {
        throw new Error("ORIGINAL_PRODUCT_MODIFIED");
      }

      // Safety checks: collisions
      if (updatedModel.products[newKey])
        throw new Error("DUPLICATE_KEY_COLLISION");
      const nextCodesLower = new Set(
        Object.values(updatedModel.products)
          .map((p) => String(p?.code || "").toLowerCase())
          .filter(Boolean),
      );
      if (nextCodesLower.has(String(newCode).toLowerCase())) {
        throw new Error("DUPLICATE_CODE_COLLISION");
      }

      updatedModel.products[newKey] = clonedProduct;

      const saveResponse = await saveCalculationsModel(updatedModel);
      if (saveResponse?.ok !== true) throw new Error("SAVE_FAILED");

      calculationsCache.set(updatedModel);
      setCalculationsModelFull(updatedModel);
      processCalculationsData(updatedModel);

      enqueueSnackbar("Produkt bol duplikovaný", { variant: "success" });
      navigate(`/calculations/products/${newKey}`);
    } catch (e) {
      console.error("Error duplicating product:", e);
      enqueueSnackbar("Nepodarilo sa duplikovať produkt", { variant: "error" });
    } finally {
      setDuplicatingProduct(null);
      setProductToDuplicate(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    setDeletingProduct(productToDelete.productKey);
    setDeleteDialogOpen(false);

    try {
      // Use GET+PUT fallback strategy for deletion
      const response = await getCalculationsModel();

      if (!response.ok || !response.data) {
        throw new Error("Failed to fetch current model");
      }

      const currentModel = response.data;

      // Remove the product from the model
      if (
        currentModel.products &&
        currentModel.products[productToDelete.productKey]
      ) {
        delete currentModel.products[productToDelete.productKey];

        // Update the generatedAt timestamp
        currentModel.generatedAt = new Date().toISOString();

        // Save the updated model
        const saveResponse = await saveCalculationsModel(currentModel);

        if (saveResponse.ok === true && saveResponse?.saved === true) {
          // Remove from local state
          setProducts((prev) =>
            prev.filter((p) => p.productKey !== productToDelete.productKey),
          );
          setFilteredProducts((prev) =>
            prev.filter((p) => p.productKey !== productToDelete.productKey),
          );

          // Clean up any local draft
          localStorage.removeItem(
            `dsok.calculations.draft.${productToDelete.productKey}`,
          );

          enqueueSnackbar("Produkt bol zmazaný.", {
            variant: "success",
            autoHideDuration: 4000,
          });
        } else {
          throw new Error("Save failed");
        }
      } else {
        throw new Error("Product not found in current model");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      enqueueSnackbar("Zmazanie sa nepodarilo.", {
        variant: "error",
        autoHideDuration: 4000,
      });
    } finally {
      setDeletingProduct(null);
      setProductToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    // Re-run the effect by triggering useEffect
    window.location.reload();
  };

  const handleClearSearch = () => {
    setSearchName("");
  };

  const handleOpenPriceListModal = () => {
    setPriceListModalOpen(true);
    setSelectedPriceType(null);
  };

  const handleClosePriceListModal = () => {
    setPriceListModalOpen(false);
    setSelectedPriceType(null);
  };

  const handleSelectPriceType = (type) => {
    setSelectedPriceType(type);
  };

  const handleBackToSelection = () => {
    setSelectedPriceType(null);
  };

  const handlePrint = () => {
    try {
      const printArea = document.getElementById("print-area");
      if (!printArea) {
        window.print();
        return;
      }

      // Print only the price list content to avoid blank pages caused by
      // printing the whole (hidden) SPA layout.
      const iframe = document.createElement("iframe");
      iframe.setAttribute("title", "pricelist-print");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) {
        window.print();
        return;
      }

      doc.open();
      doc.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Cenník</title>
  </head>
  <body>
    ${printArea.outerHTML}
  </body>
</html>`);
      doc.close();

      // Give the browser a moment to layout & load images before printing.
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } finally {
          // Cleanup after print dialog opens.
          setTimeout(() => {
            try {
              document.body.removeChild(iframe);
            } catch (_e) {}
          }, 1000);
        }
      }, 250);
    } catch (e) {
      // Fallback to default printing behavior
      window.print();
    }
  };

  const getProductPrice = (product, priceType) => {
    if (!product.calculated?.variantB) return null;

    switch (priceType) {
      case "moc":
        return product.calculated.variantB.retailEur;
      case "voc-l1":
        // wholesale.l1 might be just a number or an object with price
        const l1Price = product.calculated.variantB.wholesale?.l1;
        return typeof l1Price === "object" ? l1Price?.price : l1Price;
      case "voc-l2":
        const l2Price = product.calculated.variantB.wholesale?.l2;
        return typeof l2Price === "object" ? l2Price?.price : l2Price;
      case "voc-l3":
        const l3Price = product.calculated.variantB.wholesale?.l3;
        return typeof l3Price === "object" ? l3Price?.price : l3Price;
      default:
        return null;
    }
  };

  const getPriceTypeLabel = (priceType) => {
    switch (priceType) {
      case "moc":
        return "MOC";
      case "voc-l1":
        return "VOC L1";
      case "voc-l2":
        return "VOC L2";
      case "voc-l3":
        return "VOC L3";
      default:
        return "";
    }
  };

  // Reusable ProductImage component for consistent sizing
  const ProductImage = ({ src, alt, variant = "list" }) => {
    const heights = {
      list: 75,
      detail: 130,
      print: 60,
    };

    const height = heights[variant] || heights.list;

    if (variant === "list") {
      return (
        <img
          src={src || "/images/noImagePlaceholder.png"}
          alt={alt}
          style={{
            maxHeight: `${height}px`,
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      );
    }

    // For detail and print variants, keep the container approach
    return (
      <div
        style={{
          height: variant === "print" ? "auto" : `${height}px`,
          maxHeight: variant === "print" ? `${height}px` : "none",
          width: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={src || "/images/noImagePlaceholder.png"}
          alt={alt}
          style={{
            height: variant === "print" ? "auto" : "100%",
            maxHeight: variant === "print" ? `${height}px` : "none",
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <React.Fragment>
        <Typography variant={"h2"} mb={3}>
          {t("pages.calculations.title", {
            defaultValue: "Kalkulácie – produkty",
          })}
        </Typography>
        <CircularProgress sx={{ m: "5px 50%" }} />
      </React.Fragment>
    );
  }

  if (error) {
    return (
      <React.Fragment>
        <Typography variant={"h2"} mb={3}>
          {t("pages.calculations.title", {
            defaultValue: "Kalkulácie – produkty",
          })}
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleRetry}>
          {t("buttons.retry", { defaultValue: "Skúsiť znova" })}
        </Button>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Typography variant={"h2"} mb={3}>
        {t("pages.calculations.title", {
          defaultValue: "Kalkulácie – produkty",
        })}
      </Typography>
      <Stack
        direction="row"
        display="flex"
        alignItems="center"
        justifyContent={"space-between"}
        mb={2}
        spacing={1}
      >
        <Box width={"40%"} display="flex" flexDirection="row">
          <Card sx={{ mb: 0, width: "100%", flex: 5 }}>
            <JumboSearch
              page="Calculations"
              onChange={setSearchName}
              sx={{
                width: "100%",
              }}
              placeholder={t("pages.presentation.searchPresentation")}
            />
          </Card>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => setCreateProductModalOpen(true)}
            disabled={loading}
          >
            Pridať nový produkt
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveOrder}
            disabled={
              !(hasOrderChanges || hasPricelistVisibilityChanges) ||
              isSavingOrder
            }
            startIcon={isSavingOrder ? <CircularProgress size={16} /> : null}
          >
            {isSavingOrder ? "Ukladanie..." : "Uložiť zmeny"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleOpenPriceListModal}
            disabled={loading || !filteredProducts?.length}
          >
            Generovať cenník
          </Button>
        </Box>
      </Stack>
      <>
        {filteredProducts?.length ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredProducts.map((p) => p.productKey)}
              strategy={verticalListSortingStrategy}
            >
              {filteredProducts.map((product, index) => (
                <SortableProductItem
                  key={product.productKey}
                  product={product}
                  index={index}
                  t={t}
                  ProductImage={ProductImage}
                  getProductDisplayName={getProductDisplayName}
                  currencyCode={currencyCode}
                  defaultLanguage={defaultLanguage}
                  handleProductDetail={handleProductDetail}
                  handleDeleteClick={handleDeleteClick}
                  handleDuplicateClick={handleDuplicateClick}
                  deletingProduct={deletingProduct}
                  duplicatingProduct={duplicatingProduct}
                  onTogglePricelistVisibility={handleTogglePricelistVisibility}
                  isSavingOrder={isSavingOrder}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : searchName ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t("pages.calculations.noResults", {
                defaultValue:
                  "Nenašli sa žiadne produkty pre zadané vyhľadávanie.",
              })}
            </Typography>
            <Button variant="outlined" onClick={handleClearSearch}>
              {t("pages.calculations.clearFilter", {
                defaultValue: "Vymazať filter",
              })}
            </Button>
          </Box>
        ) : (
          <JumboListNoDataPlaceholder>
            <NoDataPlaceholder />
          </JumboListNoDataPlaceholder>
        )}
      </>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Zmazať produkt?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Naozaj chcete natrvalo zmazať tento produkt? Táto akcia sa nedá
            vrátiť späť.
          </DialogContentText>
          {productToDelete && (
            <DialogContentText sx={{ mt: 2, fontWeight: "bold" }}>
              Produkt: {productToDelete.name}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            Zrušiť
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Zmazať
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Confirmation Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={handleDuplicateCancel}
        aria-labelledby="duplicate-dialog-title"
        aria-describedby="duplicate-dialog-description"
      >
        <DialogTitle id="duplicate-dialog-title">
          Duplikovať produkt?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="duplicate-dialog-description">
            Naozaj chcete vytvoriť kópiu produktu{" "}
            <b>{productToDuplicate?.name || "—"}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDuplicateCancel}
            color="inherit"
            disabled={!!duplicatingProduct}
          >
            Zrušiť
          </Button>
          <Button
            onClick={handleDuplicateConfirm}
            variant="contained"
            disabled={!!duplicatingProduct}
          >
            Potvrdiť
          </Button>
        </DialogActions>
      </Dialog>

      {/* Price List Modal */}
      <Dialog
        open={priceListModalOpen}
        onClose={handleClosePriceListModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPriceType
            ? `Cenník - ${getPriceTypeLabel(selectedPriceType)}`
            : "Generovať cenník"}
        </DialogTitle>
        <DialogContent>
          {!selectedPriceType ? (
            // Price type selection
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                <b>MOC</b> = maloobchodný cenník, <br />
                <b>VOC</b> = veľkoobchodný cenník podľa úrovne.
                <br />
                <b>L3</b> = iKelp
                <br /> <b>L2</b> = Besteron, Storyous, Dotykačka, Agnis, Oberon
                <br /> <b>L1</b> = Všetky ostatné zľavy pre maloobchodných
                zákazníkov
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 2,
                    "& > *": {
                      flex: "1 1 calc(50% - 16px)",
                      minWidth: "200px",
                    },
                  }}
                >
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => handleSelectPriceType("moc")}
                    sx={{ py: 3 }}
                  >
                    MOC
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => handleSelectPriceType("voc-l1")}
                    sx={{ py: 3 }}
                  >
                    VOC L1
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => handleSelectPriceType("voc-l2")}
                    sx={{ py: 3 }}
                  >
                    VOC L2
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => handleSelectPriceType("voc-l3")}
                    sx={{ py: 3 }}
                  >
                    VOC L3
                  </Button>
                </Box>
                <small>
                  Cenník sa generuje z aktuálne zobrazeného zoznamu.
                </small>
              </Typography>
            </Box>
          ) : (
            // Print preview
            <Box>
              {/* <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Cenník - {selectedPriceType === "moc" ? "MOC" : "VOC"}
                </Typography>
                {selectedPriceType.startsWith("voc") && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Úroveň:{" "}
                    {selectedPriceType.replace("voc-", "").toUpperCase()}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Vygenerované: {formatDateTimeSk(new Date())}
                </Typography>
              </Box> */}

              <div id="print-area">
                <style jsx>{`
                  @media print {
                    @page {
                      margin: 16mm 10mm 22mm 10mm;
                    }
                    body * {
                      visibility: hidden;
                    }
                    #print-area,
                    #print-area * {
                      visibility: visible;
                    }
                    #print-area {
                      position: absolute;
                      left: 0;
                      top: 0;
                      width: 100%;
                      /* reserve space for fixed header/footer */
                      padding-top: 56px;
                      padding-bottom: 84px;
                      box-sizing: border-box;
                    }
                    .print-only {
                      display: block !important;
                    }
                    .print-header-fixed {
                      position: fixed;
                      top: 0;
                      left: 0;
                      right: 0;
                      height: 48px;
                      padding: 8px 10mm;
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      box-sizing: border-box;
                      background: white;
                    }
                    .print-logo {
                      display: inline-flex;
                      align-items: center;
                      justify-content: center;
                      width: 120px;
                      height: 32px;
                      border-radius: 0;
                      background: transparent;
                      flex: 0 0 auto;
                    }
                    .print-logo img {
                      height: 28px;
                      width: auto;
                      display: block;
                    }
                    .print-title {
                      font-size: 14px;
                      font-weight: 700;
                      color: #111827;
                      text-align: right;
                      margin-left: 12px;
                      flex: 1 1 auto;
                      /* Align title with right-aligned price text (cell padding) */
                      padding-right: 8px;
                    }
                    .print-footer-fixed {
                      position: fixed;
                      left: 0;
                      right: 0;
                      bottom: 0;
                      padding: 6px 10mm 8px 10mm;
                      box-sizing: border-box;
                      background: white;
                      border-top: 1px solid #d1d5db;
                      color: #6b7280;
                      font-size: 9.5px;
                      line-height: 1.25;
                    }
                    .print-footer-row {
                      display: flex;
                      flex-wrap: wrap;
                      gap: 6px 14px;
                      align-items: baseline;
                      justify-content: space-between;
                    }
                    .print-footer-row > div {
                      min-width: 0;
                    }
                    .print-header {
                      page-break-after: avoid;
                    }
                    table {
                      page-break-inside: auto;
                      font-size: 10px;
                    }
                    tr {
                      page-break-inside: avoid;
                      page-break-after: auto;
                    }
                    thead {
                      display: table-header-group;
                    }
                    tfoot {
                      display: table-footer-group;
                    }
                    td div {
                      font-size: 9px !important;
                      line-height: 1.2 !important;
                      max-height: none !important;
                      overflow: visible !important;
                    }
                    /* Make Popis align consistently (incl. HTML lists) */
                    #print-area .print-desc,
                    #print-area .print-desc * {
                      text-align: left !important;
                    }
                    #print-area .print-desc {
                      /* Align plain text with list item text */
                      padding-left: 14px !important;
                      box-sizing: border-box;
                    }
                    #print-area .print-desc p,
                    #print-area .print-desc div {
                      margin: 0 !important;
                    }
                    #print-area .print-desc ul,
                    #print-area .print-desc ol {
                      margin: 0 !important;
                      padding-left: 14px !important;
                      list-style-position: outside !important;
                    }
                    #print-area .print-desc li {
                      margin: 0 !important;
                      padding: 0 !important;
                    }
                  }
                `}</style>

                <div
                  className="print-header-fixed print-only"
                  style={{ display: "none" }}
                >
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div className="print-logo" aria-hidden="true">
                      <img src="/images/logo.png" alt="DataVision" />
                    </div>
                  </div>
                </div>

                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "12px",
                  }}
                >
                  <thead className="print-header">
                    <tr>
                      <th
                        style={{
                          border: "1px solid #ddd",
                          padding: "8px",
                          textAlign: "left",
                          width: "80px",
                        }}
                      ></th>
                      <th
                        style={{
                          border: "1px solid #ddd",
                          padding: "8px",
                          textAlign: "left",
                        }}
                      >
                        Produkt
                      </th>
                      <th
                        style={{
                          border: "1px solid #ddd",
                          padding: "8px",
                          textAlign: "left",
                        }}
                      >
                        Popis
                      </th>
                      <th
                        style={{
                          border: "1px solid #ddd",
                          padding: "8px",
                          textAlign: "right",
                          width: selectedPriceType.startsWith("voc")
                            ? "80px"
                            : "100px",
                        }}
                      >
                        MOC
                      </th>
                      {selectedPriceType.startsWith("voc") && (
                        <th
                          style={{
                            border: "1px solid #ddd",
                            padding: "8px",
                            textAlign: "right",
                            width: "80px",
                          }}
                        >
                          VOC
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const visibleProducts = filteredProducts.filter(
                        (product) =>
                          normalizeIsVisibleInPricelist(
                            product.isVisibleInPricelist,
                          ),
                      );
                      console.log(
                        "DEBUG: Pricelist filtering - Total products:",
                        filteredProducts.length,
                      );
                      console.log(
                        "DEBUG: Pricelist filtering - Visible products:",
                        visibleProducts.length,
                      );
                      filteredProducts.forEach((product) => {
                        console.log(
                          `DEBUG: Product ${product.name} (${
                            product.productKey
                          }): isVisibleInPricelist=${
                            product.isVisibleInPricelist
                          }, normalized=${normalizeIsVisibleInPricelist(
                            product.isVisibleInPricelist,
                          )}`,
                        );
                      });
                      return visibleProducts;
                    })().map((product) => {
                      const mocPrice =
                        product.calculated?.variantB?.retailEur ?? null;
                      const vocPrice = selectedPriceType.startsWith("voc")
                        ? getProductPrice(product, selectedPriceType)
                        : null;
                      const description =
                        product.description?.html ||
                        product.description?.text ||
                        "—";

                      return (
                        <tr key={product.productKey}>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "8px",
                              textAlign: "center",
                            }}
                          >
                            {product.imageUrl && (
                              <ProductImage
                                src={product.imageUrl}
                                alt={product.name}
                                variant="print"
                              />
                            )}
                          </td>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "8px",
                              fontWeight: "bold",
                            }}
                          >
                            {product.name}
                          </td>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "8px",
                              maxWidth: "300px",
                              verticalAlign: "top",
                            }}
                          >
                            {description === "—" ? (
                              description
                            ) : (
                              <div
                                className="print-desc"
                                dangerouslySetInnerHTML={{
                                  __html: description,
                                }}
                                style={{
                                  fontSize: "11px",
                                  lineHeight: "1.3",
                                  // Do not truncate description in price list/print
                                  textAlign: "left",
                                  whiteSpace: "normal",
                                  overflow: "visible",
                                  maxHeight: "none",
                                  wordBreak: "break-word",
                                }}
                              />
                            )}
                          </td>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "8px",
                              textAlign: "right",
                              fontWeight: "bold",
                              width: selectedPriceType.startsWith("voc")
                                ? "80px"
                                : "100px",
                            }}
                          >
                            {mocPrice !== null
                              ? `${Math.round(mocPrice)} €`
                              : "—"}
                          </td>
                          {selectedPriceType.startsWith("voc") && (
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "right",
                                fontWeight: "bold",
                                width: "80px",
                              }}
                            >
                              {vocPrice !== null
                                ? `${Math.round(vocPrice)} €`
                                : "—"}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div
                  className="print-footer-fixed print-only"
                  style={{ display: "none" }}
                >
                  <div className="print-footer-row">
                    <div>
                      Simpleo s.r.o., Slnečná 1911/1, 922 03 Vrbové, Slovakia,
                      info@predajnykiosk.sk, +421 918 755 096
                    </div>
                    <div>
                      Ceny sú uvedené bez DPH 23%, v cene nie je platobný
                      terminál, Záručná doba je 12 mesiacov, ak nie je uvedené
                      inak.
                    </div>
                    <div>Cenník je platný od 1.1. {currentYear}</div>
                  </div>
                </div>
              </div>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedPriceType ? (
            <>
              <Button onClick={handleBackToSelection}>Späť</Button>
              <Button onClick={handlePrint} variant="contained">
                Tlačiť
              </Button>
            </>
          ) : (
            <Button onClick={handleClosePriceListModal}>Zrušiť</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Create Product Modal */}
      <Dialog
        open={createProductModalOpen}
        onClose={() => !isCreatingProduct && setCreateProductModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Vytvoriť nový produkt</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Názov produktu"
              value={newProductData.name}
              onChange={(e) =>
                setNewProductData((prev) => ({ ...prev, name: e.target.value }))
              }
              error={!!newProductErrors.name}
              helperText={newProductErrors.name}
              fullWidth
              required
            />
            <TextField
              label="Obrázok (URL)"
              value={newProductData.image}
              onChange={(e) =>
                setNewProductData((prev) => ({
                  ...prev,
                  image: e.target.value,
                }))
              }
              error={!!newProductErrors.image}
              helperText={newProductErrors.image}
              fullWidth
              required
              placeholder="https://..."
            />
            <TextField
              label="URL (voliteľné)"
              value={newProductData.url}
              onChange={(e) =>
                setNewProductData((prev) => ({ ...prev, url: e.target.value }))
              }
              error={!!newProductErrors.url}
              helperText={newProductErrors.url}
              fullWidth
              placeholder="https://..."
            />
            <TextField
              label="Kód produktu"
              value={newProductData.code}
              onChange={(e) =>
                setNewProductData((prev) => ({ ...prev, code: e.target.value }))
              }
              error={!!newProductErrors.code}
              helperText={newProductErrors.code}
              fullWidth
              required
              placeholder="PARTNERTECH-STAN-HOLDER"
            />
            <FormControl
              fullWidth
              required
              error={!!newProductErrors.productType}
            >
              <InputLabel>Typ produktu</InputLabel>
              <Select
                value={newProductData.productType}
                onChange={(e) =>
                  setNewProductData((prev) => ({
                    ...prev,
                    productType: e.target.value,
                  }))
                }
                label="Typ produktu"
              >
                <MenuItem value="kiosk">kiosk</MenuItem>
                <MenuItem value="accessories">accessories</MenuItem>
              </Select>
              {newProductErrors.productType && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 2 }}
                >
                  {newProductErrors.productType}
                </Typography>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateProductModalOpen(false);
              setNewProductData({
                name: "",
                image: "",
                url: "",
                code: "",
                productType: "",
              });
              setNewProductErrors({});
            }}
            disabled={isCreatingProduct}
          >
            Zrušiť
          </Button>
          <Button
            onClick={handleCreateProduct}
            variant="contained"
            disabled={
              isCreatingProduct ||
              Object.values(newProductErrors).some((error) => error)
            }
            startIcon={
              isCreatingProduct ? <CircularProgress size={16} /> : null
            }
          >
            {isCreatingProduct ? "Vytváranie..." : "Vytvoriť"}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default Calculations;
